import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search, Shield, Star, BadgeCheck, MapPin, Clock, Users, Heart, Sparkles, Lock, MessageCircle, Calendar, ArrowRight } from "lucide-react";

const CITIES = [
  { name: "Paris", slug: "paris" },
  { name: "Lyon", slug: "lyon" },
  { name: "Marseille", slug: "marseille" },
  { name: "Toulouse", slug: "toulouse" },
  { name: "Bordeaux", slug: "bordeaux" },
  { name: "Lille", slug: "lille" },
  { name: "Nantes", slug: "nantes" },
  { name: "Nice", slug: "nice" },
  { name: "Strasbourg", slug: "strasbourg" },
  { name: "Rennes", slug: "rennes" },
  { name: "Montpellier", slug: "montpellier" },
  { name: "Grenoble", slug: "grenoble" },
];

const FAQS = [
  {
    q: "Combien coûte une baby-sitter en France ?",
    a: "Le tarif moyen d'une baby-sitter en France se situe entre 10 € et 15 € de l'heure selon la ville, l'expérience et les qualifications de la nounou. Sur Click&Gard, chaque professionnel fixe librement son tarif horaire et vous voyez le prix avant de réserver, sans frais cachés. Les grandes villes comme Paris, Lyon ou Bordeaux affichent généralement des tarifs un peu plus élevés que la moyenne nationale.",
  },
  {
    q: "Comment réserver une nounou sur Click&Gard ?",
    a: "Réserver une nounou prend moins de 3 minutes : créez un compte parent gratuit, recherchez une baby-sitter dans votre ville, consultez son profil vérifié et ses avis, puis réservez le créneau souhaité directement en ligne. La professionnelle reçoit votre demande instantanément et vous pouvez échanger avec elle via la messagerie intégrée. Le règlement de la garde se fait ensuite directement entre vous et la nounou.",
  },
  {
    q: "Est-ce sécurisé de passer par Click&Gard ?",
    a: "Oui, la sécurité est notre priorité absolue. Chaque baby-sitter est soumise à une vérification d'identité (pièce d'identité + selfie), à une validation manuelle par notre équipe et à un abonnement actif. Click&Gard ne gère pas les paiements de garde : le règlement se fait directement entre le parent et la nounou. Les avis vérifiés des parents et notre service de modération garantissent une mise en relation de confiance.",
  },
  {
    q: "Une garde d'enfant en urgence est-elle possible ?",
    a: "Oui, Click&Gard permet de trouver une baby-sitter en urgence grâce à la recherche par disponibilité immédiate. Filtrez les profils disponibles dès aujourd'hui ou ce soir, contactez plusieurs nounous via la messagerie et obtenez une réponse en quelques minutes. De nombreux professionnels acceptent des gardes de dernière minute, en journée comme en soirée.",
  },
  {
    q: "Comment devenir baby-sitter sur Click&Gard ?",
    a: "Pour devenir baby-sitter sur Click&Gard, créez un compte professionnel, complétez votre profil (expérience, diplômes, tarif, zone d'intervention), envoyez vos pièces justificatives pour la vérification d'identité et souscrivez à l'abonnement professionnel. Une fois validé par notre équipe, vous recevez les demandes de garde d'enfants des parents de votre ville et gérez librement votre planning.",
  },
];

const BabysitterFrance = () => {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const localBusinessJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Click&Gard",
    description: "Plateforme de mise en relation entre parents et baby-sitters vérifiées partout en France.",
    url: "https://clickandgard.fr/babysitter-france",
    image: "https://clickandgard.fr/og-image.jpg",
    areaServed: { "@type": "Country", name: "France" },
    priceRange: "€€",
    serviceType: "Garde d'enfants à domicile",
  };

  return (
    <div className="min-h-screen bg-immersive">
      <Seo
        title="Babysitter France | Garde d'enfant fiable"
        description="Trouvez une baby-sitter ou nounou de confiance partout en France. Profils vérifiés, réservation en ligne, 0% commission. La garde d'enfant simple et rapide avec Click&Gard."
        path="/babysitter-france"
        jsonLd={[faqJsonLd, localBusinessJsonLd]}
      />
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden py-20 md:py-28 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-pulse" />

        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-primary">Disponible partout en France</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-display font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
            Trouver une baby-sitter partout en France
          </h1>
          <p className="text-lg md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            La garde d'enfant simple, rapide et sécurisée partout en France. Profils vérifiés, 0% commission, réservation en quelques clics.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/search">
              <Button size="lg" variant="neon" className="text-base font-semibold">
                <Search className="w-5 h-5" />
                Trouver une babysitter
              </Button>
            </Link>
            <Link to="/auth?tab=register">
              <Button size="lg" variant="outline" className="text-base font-semibold">
                Devenir baby-sitter
              </Button>
            </Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-10 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-primary" /> Profils vérifiés</span>
            <span className="flex items-center gap-2"><Lock className="w-4 h-4 text-primary" /> 0% commission</span>
            <span className="flex items-center gap-2"><Heart className="w-4 h-4 text-primary" /> 100% gratuit pour les parents</span>
          </div>
        </div>
      </section>

      {/* TEXTE SEO PRINCIPAL */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto prose prose-invert prose-lg">
          <article className="space-y-6 text-foreground/90">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
              La plateforme de référence pour la garde d'enfant en France
            </h2>
            <p>
              Trouver une <strong>baby-sitter</strong> de confiance en France n'a jamais été aussi simple. Click&Gard est la plateforme française dédiée à la mise en relation entre parents et professionnels de la <strong>garde d'enfant</strong>, partout sur le territoire. Que vous habitiez à Paris, Lyon, Marseille, Bordeaux ou dans une petite commune, vous accédez à des centaines de profils de <strong>nounous</strong>, baby-sitters et assistantes maternelles vérifiées en quelques clics.
            </p>
            <p>
              Notre mission : rendre le <strong>babysitting</strong> accessible, sécurisé et transparent pour toutes les familles françaises. Click&Gard est 100% gratuit pour les parents : pas d'abonnement, pas de frais cachés. Vous payez uniquement la prestation de garde, au tarif fixé par la baby-sitter.
            </p>

            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground pt-6">
              Comment fonctionne Click&Gard ?
            </h2>
            <p>
              Notre plateforme repose sur un fonctionnement simple en 4 étapes. <strong>Premièrement</strong>, vous créez un compte parent gratuit en moins d'une minute. <strong>Deuxièmement</strong>, vous lancez une recherche en indiquant votre ville, le créneau souhaité et le nombre d'enfants à garder. <strong>Troisièmement</strong>, vous parcourez les profils de baby-sitters disponibles : photo, expérience, diplômes, tarif horaire, avis vérifiés, zone d'intervention. <strong>Quatrièmement</strong>, vous réservez le créneau en ligne et convenez du mode de règlement directement avec la nounou.
            </p>
            <p>
              Avant la réservation, vous pouvez échanger librement avec la nounou via notre messagerie intégrée pour vérifier sa disponibilité, poser vos questions et faire connaissance. Une fois la garde d'enfant terminée, vous validez la prestation et laissez un avis pour aider la communauté de parents.
            </p>

            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground pt-6">
              Sécurité et vérification des profils
            </h2>
            <p>
              La sécurité de vos enfants est notre priorité absolue. Chaque baby-sitter inscrite sur Click&Gard passe par un processus de vérification rigoureux en trois étapes : <strong>vérification d'email</strong>, <strong>contrôle d'identité</strong> (pièce d'identité officielle et selfie) et <strong>validation manuelle</strong> par notre équipe de modération. Seuls les professionnels ayant souscrit à un abonnement actif peuvent recevoir des demandes de garde, ce qui garantit leur engagement et leur sérieux.
            </p>
            <p>
              Click&Gard ne gère pas les paiements de garde : le règlement se fait directement entre le parent et la baby-sitter (espèces, CESU, virement, PayPal, etc.). Notre rôle est de garantir une mise en relation sérieuse grâce à la vérification d'identité, à la modération des profils et des messages, et à un service de médiation rapide en cas de litige.
            </p>

            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground pt-6">
              Une couverture nationale, partout en France
            </h2>
            <p>
              Click&Gard couvre l'ensemble du territoire français : grandes métropoles, villes moyennes et zones rurales. Que vous cherchiez une <strong>nounou à Paris</strong> pour une sortie en soirée, une baby-sitter à Lyon pour la sortie d'école, une garde d'enfant à Marseille pendant les vacances scolaires ou une assistante maternelle à Toulouse en urgence, vous trouverez rapidement la perle rare. Notre algorithme de géolocalisation affiche en priorité les professionnels proches de chez vous, dans un rayon que vous définissez.
            </p>
            <p>
              Le <strong>babysitting</strong> en France évolue, et Click&Gard accompagne cette transformation en proposant une solution moderne, digitale et humaine. Nous croyons qu'une garde d'enfant réussie repose sur la confiance, la transparence et la simplicité. C'est pourquoi nous mettons tout en œuvre pour que chaque mise en relation soit une expérience positive, pour les parents comme pour les professionnels.
            </p>

            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground pt-6">
              Réservation en ligne et flexibilité
            </h2>
            <p>
              Avec Click&Gard, vous réservez votre baby-sitter en ligne 24h/24, 7j/7. Plus besoin de passer des dizaines d'appels ou de chercher dans votre carnet d'adresses : tout se fait depuis votre smartphone ou votre ordinateur. Vous pouvez réserver une garde ponctuelle, régulière (sortie d'école, périscolaire), de soirée, de week-end ou pendant les vacances. Certaines nounous acceptent même des gardes en urgence, le jour même.
            </p>
          </article>
        </div>
      </section>

      {/* POURQUOI CHOISIR */}
      <section className="py-16 px-4 bg-card/30 backdrop-blur-sm border-y border-border/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-4">
            Pourquoi choisir Click&Gard ?
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            La solution de référence pour la garde d'enfant partout en France
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: BadgeCheck, title: "Profils 100% vérifiés", desc: "Identité, diplômes et expérience contrôlés manuellement par notre équipe." },
              { icon: Lock, title: "0% commission", desc: "Vous réglez la nounou directement. Click&Gard ne prélève rien sur la prestation." },
              { icon: Star, title: "Avis vérifiés", desc: "Consultez les notes et commentaires laissés par d'autres parents." },
              { icon: Clock, title: "Disponibilité immédiate", desc: "Trouvez une baby-sitter en quelques minutes, même en urgence." },
              { icon: Heart, title: "Gratuit pour les parents", desc: "Aucun abonnement, aucun frais caché. Vous payez la prestation, c'est tout." },
              { icon: MapPin, title: "Partout en France", desc: "Une couverture nationale, du centre-ville aux zones rurales." },
            ].map((item) => (
              <article key={item.title} className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm p-6 hover:border-primary/40 transition-all hover:-translate-y-1">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-4 shadow-lg">
                  <item.icon className="w-6 h-6 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* COMMENT CA MARCHE */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12">
            Comment ça marche ?
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { n: "1", icon: Users, title: "Créez votre compte", desc: "Inscription gratuite en moins d'une minute." },
              { n: "2", icon: Search, title: "Cherchez une nounou", desc: "Filtrez par ville, créneau et critères." },
              { n: "3", icon: MessageCircle, title: "Échangez et réservez", desc: "Discutez via la messagerie puis confirmez." },
              { n: "4", icon: Calendar, title: "Profitez de la garde", desc: "Vous réglez la nounou directement après la prestation." },
            ].map((step) => (
              <div key={step.n} className="relative text-center">
                <div className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary text-primary-foreground font-bold flex items-center justify-center shadow-lg">
                  {step.n}
                </div>
                <div className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm p-6 h-full">
                  <step.icon className="w-10 h-10 text-primary mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENGAGEMENTS SECURITE */}
      <section className="py-16 px-4 bg-card/30 backdrop-blur-sm border-y border-border/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">Nos engagements sécurité</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Confier ses enfants n'est pas anodin. Voici comment nous garantissons votre tranquillité.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Vérification d'identité obligatoire", desc: "Pièce d'identité et selfie contrôlés pour chaque baby-sitter." },
              { title: "Validation manuelle des profils", desc: "Notre équipe examine chaque dossier avant activation du compte." },
              { title: "0% commission sur la garde", desc: "Le règlement se fait directement entre le parent et la nounou." },
              { title: "Service de médiation", desc: "En cas de litige, nous intervenons rapidement pour vous protéger." },
              { title: "Avis vérifiés uniquement", desc: "Seuls les parents ayant réservé peuvent laisser un avis." },
              { title: "Données chiffrées et RGPD", desc: "Vos données personnelles sont protégées selon les normes européennes." },
            ].map((item) => (
              <div key={item.title} className="flex gap-4 p-5 rounded-xl bg-background/50 border border-border/50">
                <BadgeCheck className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VILLES */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-4">
            Trouver une babysitter dans votre ville
          </h2>
          <p className="text-center text-muted-foreground mb-12">
            Click&Gard est disponible partout en France. Découvrez les baby-sitters près de chez vous.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {CITIES.map((city) => (
              <Link
                key={city.slug}
                to={`/baby-sitter/${city.slug}`}
                className="group flex items-center justify-between p-4 rounded-xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/40 hover:bg-primary/5 transition-all"
              >
                <span className="font-medium">
                  Baby-sitter <span className="text-primary">{city.name}</span>
                </span>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/baby-sitter" className="px-4 py-2 rounded-full bg-primary/10 border border-primary/30 text-sm font-medium hover:bg-primary/20 transition-all">Baby-sitter</Link>
            <Link to="/nounou" className="px-4 py-2 rounded-full bg-primary/10 border border-primary/30 text-sm font-medium hover:bg-primary/20 transition-all">Nounou à domicile</Link>
            <Link to="/garde-enfants" className="px-4 py-2 rounded-full bg-primary/10 border border-primary/30 text-sm font-medium hover:bg-primary/20 transition-all">Garde d'enfants</Link>
            <Link to="/assistante-maternelle" className="px-4 py-2 rounded-full bg-primary/10 border border-primary/30 text-sm font-medium hover:bg-primary/20 transition-all">Assistante maternelle</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-card/30 backdrop-blur-sm border-y border-border/50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-4">
            Questions fréquentes
          </h2>
          <p className="text-center text-muted-foreground mb-12">
            Tout ce que vous devez savoir sur la garde d'enfant avec Click&Gard
          </p>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="rounded-xl border border-border/50 bg-card/50 backdrop-blur-sm px-5 border-b"
              >
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 p-12 backdrop-blur-sm">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
          <div className="relative">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
              Prêt à trouver votre baby-sitter ?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Rejoignez les milliers de parents qui font confiance à Click&Gard pour la garde de leurs enfants partout en France.
            </p>
            <Link to="/search">
              <Button size="lg" variant="neon" className="text-base font-semibold">
                <Search className="w-5 h-5" />
                Trouver une babysitter maintenant
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BabysitterFrance;
